<template>
  <span
    class="gh-action-tooltip-host"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <slot />
    <span
      v-if="visible"
      ref="tooltip"
      class="gh-action-tooltip"
      role="tooltip"
      :style="popupStyle"
    >{{ text }}</span>
  </span>
</template>

<script>
const EDGE = 8;
const GAP = 6;

export default {
  name: 'GenerationHistoryActionTooltip',
  props: {
    text: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      visible: false,
      popupStyle: {
        visibility: 'hidden',
      },
      positionListenersBound: false,
    };
  },
  beforeDestroy() {
    this.unbindPositionListeners();
  },
  methods: {
    onEnter() {
      if (!this.text) {
        return;
      }
      this.popupStyle = { visibility: 'hidden' };
      this.visible = true;
      this.bindPositionListeners();
      this.$nextTick(this.updatePosition);
    },
    onLeave() {
      this.visible = false;
      this.unbindPositionListeners();
    },
    bindPositionListeners() {
      if (this.positionListenersBound) {
        return;
      }
      window.addEventListener('scroll', this.onReposition, true);
      window.addEventListener('resize', this.onReposition);
      this.positionListenersBound = true;
    },
    unbindPositionListeners() {
      if (!this.positionListenersBound) {
        return;
      }
      window.removeEventListener('scroll', this.onReposition, true);
      window.removeEventListener('resize', this.onReposition);
      this.positionListenersBound = false;
    },
    onReposition() {
      if (!this.visible) {
        return;
      }
      this.updatePosition();
    },
    updatePosition() {
      const host = this.$el;
      const tip = this.$refs.tooltip;
      if (!host || !tip) {
        return;
      }
      const rect = host.getBoundingClientRect();
      // Host scrolled out of viewport — hide to avoid orphaned fixed tip
      if (
        rect.bottom < 0
        || rect.top > window.innerHeight
        || rect.right < 0
        || rect.left > window.innerWidth
      ) {
        this.visible = false;
        this.unbindPositionListeners();
        return;
      }

      const tipWidth = tip.offsetWidth || 0;
      const tipHeight = tip.offsetHeight || 0;
      let left = rect.left + (rect.width / 2) - (tipWidth / 2);
      if (left < EDGE) {
        left = EDGE;
      }
      const maxLeft = window.innerWidth - tipWidth - EDGE;
      if (left > maxLeft) {
        left = Math.max(EDGE, maxLeft);
      }

      const aboveVisualTop = rect.top - GAP - tipHeight;
      const belowVisualBottom = rect.bottom + GAP + tipHeight;
      const canAbove = aboveVisualTop >= EDGE;
      const canBelow = belowVisualBottom <= window.innerHeight - EDGE;

      let top;
      let transform;
      if (canAbove) {
        top = rect.top - GAP;
        transform = 'translateY(-100%)';
      } else if (canBelow) {
        top = rect.bottom + GAP;
        transform = 'none';
      } else {
        // Neither side fits fully — clamp absolute top inside viewport
        top = Math.min(
          Math.max(EDGE, rect.top - GAP - tipHeight),
          Math.max(EDGE, window.innerHeight - tipHeight - EDGE),
        );
        transform = 'none';
      }

      this.popupStyle = {
        top: `${top}px`,
        left: `${left}px`,
        transform,
        visibility: 'visible',
      };
    },
  },
};
</script>
